// ╔══════════════════════════════════════════════════════════════╗
// ║  Production Review — accountant approves/rejects entries      ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api.js';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR, fmtDateShort } from '../../lib/format.js';
import { Badge, Btn, Card, Field, Input, Modal } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';

export default function ProductionReviewPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['production', 'entries', { status: 'pending' }],
    queryFn: () =>
      api
        .get('/production/entries', { params: { status: 'pending', pageSize: 100 } })
        .then((r) => r.data),
  });

  const approveMut = useMutation({
    mutationFn: (id) => api.post(`/production/entries/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'entries'] });
      toast.push({ tone: 'success', title: 'تم الاعتماد' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) =>
      api.post(`/production/entries/${id}/reject`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'entries'] });
      toast.push({ tone: 'success', title: 'تم الرفض' });
      setRejecting(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  const approveBulkMut = useMutation({
    mutationFn: (ids) =>
      api.post('/production/entries/approve-bulk', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['production', 'entries'] });
      toast.push({
        tone: 'success',
        title: `تم اعتماد ${res.data.approvedCount} إدخال`,
      });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  const entries = data?.entries ?? [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            المحاسب
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">
            مراجعة إدخالات الإنتاج
            <span className="text-ink-400 font-bold mr-2 tabular">· {fmtAR(entries.length)}</span>
          </h1>
        </div>
        {entries.length > 0 && (
          <Btn
            variant="success"
            icon="check"
            loading={approveBulkMut.isPending}
            onClick={() => approveBulkMut.mutate(entries.map((e) => e.id))}
          >
            اعتماد الكل
          </Btn>
        )}
      </div>

      {isLoading ? (
        <Card className="p-8 text-center text-ink-400">جاري التحميل…</Card>
      ) : entries.length === 0 ? (
        <Card className="p-12 text-center">
          <Icon name="check" size={48} className="mx-auto text-emerald-400 mb-3" />
          <p className="text-ink-500 font-bold">لا توجد إدخالات تنتظر المراجعة</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
                <tr>
                  {['التاريخ', 'العامل', 'المرحلة', 'الهدف', 'الفعلي', 'الأثر', 'إجراء'].map(
                    (h, i) => (
                      <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-soft">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    <td className="px-4 py-3 font-mono text-xs">{fmtDateShort(e.date)}</td>
                    <td className="px-4 py-3 font-semibold">{e.workerName ?? e.workerId}</td>
                    <td className="px-4 py-3">{e.stageId}</td>
                    <td className="px-4 py-3 tabular">{fmtAR(e.target)}</td>
                    <td className="px-4 py-3 tabular font-extrabold">{fmtAR(e.actual)}</td>
                    <td className="px-4 py-3">
                      {Number(e.delta) > 0 ? (
                        <Badge tone="success">+{fmtAR(e.delta)} ج</Badge>
                      ) : (
                        <Badge tone="danger">{fmtAR(e.delta)} ج</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex gap-1">
                        <Btn
                          size="sm"
                          variant="success"
                          icon="check"
                          onClick={() => approveMut.mutate(e.id)}
                        >
                          اعتماد
                        </Btn>
                        <Btn
                          size="sm"
                          variant="danger"
                          icon="x"
                          onClick={() => setRejecting(e)}
                        >
                          رفض
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {rejecting && (
        <RejectModal
          entry={rejecting}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) =>
            rejectMut.mutate({ id: rejecting.id, reason })
          }
          loading={rejectMut.isPending}
        />
      )}
    </div>
  );
}

function RejectModal({ entry, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      open
      onClose={onClose}
      title="رفض إدخال إنتاج"
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn
            variant="danger"
            icon="x"
            disabled={!reason.trim()}
            loading={loading}
            onClick={() => onSubmit(reason)}
          >
            تأكيد الرفض
          </Btn>
        </>
      }
    >
      <p className="text-sm mb-3">
        رفض إدخال للعامل <strong>{entry.workerName ?? entry.workerId}</strong> بتاريخ {fmtDateShort(entry.date)}
      </p>
      <Field label="سبب الرفض">
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="اكتب سبب الرفض"
          required
        />
      </Field>
    </Modal>
  );
}
